import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TrackingService } from './tracking.service';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    gtag: (...args: [string, ...unknown[]]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly tracking = inject(TrackingService);
  private readonly enabled: boolean;

  constructor() {
    this.enabled = isPlatformBrowser(inject(PLATFORM_ID)) && !!environment.gaMeasurementId;
  }

  // ── Core ──────────────────────────────────────────────────

  private event(name: string, params: Record<string, string | number | boolean> = {}): void {
    if (!this.enabled) return;
    window.gtag('event', name, { ...this.baseParams(), ...params });
  }

  private baseParams(): Record<string, string> {
    const data = this.tracking.getTrackingData();
    return { segment: data.segment, device_type: data.deviceType };
  }

  // ── A. User Analytics ─────────────────────────────────────

  trackPageView(pagePath: string, pageTitle: string): void {
    this.event('page_view', { page_path: pagePath, page_title: pageTitle });
  }

  trackSessionStart(): void {
    if (!this.enabled) return;
    const data = this.tracking.getTrackingData();
    // Set user-scoped segment property
    window.gtag('set', 'user_properties', { user_segment: data.segment });
    this.event('session_start', {
      utm_source: data.utmSource,
      utm_medium: data.utmMedium,
      utm_campaign: data.utmCampaign,
      referrer: data.referrer,
    });
  }

  // ── B. Behaviour Analytics ────────────────────────────────

  trackScrollDepth(depth: 25 | 50 | 75 | 100, pagePath: string): void {
    this.event('scroll_depth', { depth_threshold: depth, page_path: pagePath });
  }

  trackSectionView(sectionId: string): void {
    this.event('section_view', { section_id: sectionId });
  }

  trackCtaClick(ctaId: string, ctaLocation: string): void {
    this.event('cta_click', { cta_id: ctaId, cta_location: ctaLocation });
  }

  trackFaqExpand(questionIndex: number, questionText: string): void {
    this.event('faq_expand', {
      question_index: questionIndex,
      question_text: questionText.slice(0, 100),
    });
  }

  trackBlogRead(slug: string, title: string): void {
    this.event('blog_read', { slug, title });
  }

  trackBlogReadTime(slug: string, seconds: number): void {
    if (seconds < 5 || seconds > 1800) return;
    this.event('blog_read_time', { slug, seconds: Math.round(seconds) });
  }

  trackServiceView(slug: string, title: string): void {
    this.event('service_view', { slug, title });
  }

  trackDestinationView(city: string): void {
    this.event('destination_view', { city });
  }

  trackClinicView(city: string, clinic: string): void {
    this.event('clinic_view', { city, clinic });
  }

  trackExploreCardClick(cardTitle: string, route: string): void {
    this.event('explore_card_click', { card_title: cardTitle, route });
  }

  trackOutboundClick(url: string, linkType: 'whatsapp' | 'phone' | 'external'): void {
    this.event('outbound_click', { url, link_type: linkType });
  }

  // ── C. Lead Funnel Analytics ──────────────────────────────

  trackFormView(): void {
    this.event('form_view');
  }

  trackFormStart(): void {
    this.event('form_start');
  }

  trackFormContactMethod(method: 'phone' | 'email'): void {
    this.event('form_contact_method', { method });
  }

  trackFormSubmit(contactMethod: 'phone' | 'email'): void {
    this.event('form_submit', { contact_method: contactMethod });
  }

  trackFormSuccess(contactMethod: 'phone' | 'email'): void {
    this.event('form_success', { contact_method: contactMethod });
  }

  trackFormError(errorMessage: string): void {
    this.event('form_error', { error_message: errorMessage.slice(0, 200) });
  }
}
